import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import {
  AdminLinkEmailCredentialMutation,
  AdminLinkEmailCredentialMutationVariables,
  AdminUserSummaryFieldsFragment,
} from '../../../../__gqltypes__';
import { ADMIN_LINK_EMAIL_CREDENTIAL } from '../recovery/graphql';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  user: AdminUserSummaryFieldsFragment;
  onHide: () => void;
  onSuccess: () => void;
};

export default function LinkEmailCredentialModal({ user, onHide, onSuccess }: Props): JSX.Element {
  const [newEmail, setNewEmail] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const [linkEmail, { loading }] = useMutation<
    AdminLinkEmailCredentialMutation,
    AdminLinkEmailCredentialMutationVariables
  >(ADMIN_LINK_EMAIL_CREDENTIAL);

  const trimmedEmail = newEmail.trim();
  const emailValid = EMAIL_REGEX.test(trimmedEmail);
  const canSubmit = emailValid && acknowledged && !loading;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      const { data } = await linkEmail({
        variables: { input: { userId: user.userId, newEmail: trimmedEmail } },
      });
      if (data?.adminLinkEmailCredential.success) {
        setResult({
          variant: 'success',
          text: data.adminLinkEmailCredential.message ?? 'Email credential linked.',
        });
        setTimeout(() => {
          onSuccess();
          onHide();
        }, 1500);
      } else {
        setResult({
          variant: 'danger',
          text: data?.adminLinkEmailCredential.message ?? 'Unknown error.',
        });
      }
    } catch (err) {
      setResult({ variant: 'danger', text: (err as Error).message });
    }
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Change email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="py-2">
          <strong>Verify identity first.</strong>
        </Alert>

        <div className="mb-3 small">
          <div>
            <strong>User:</strong> {user.displayName} (<code>{user.userId}</code>)
          </div>
          <div>
            <strong>Email:</strong> {user.email ?? <em className="text-muted">(none)</em>}
          </div>
          <div>
            <strong>Firebase:</strong> {user.firebaseEmail ?? <em className="text-muted">(none)</em>}
          </div>
          <div>
            <strong>Providers:</strong>{' '}
            {user.providers.length > 0 ? user.providers.map((p) => p.type).join(', ') : <em>(none)</em>}
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label>New email</Form.Label>
            <Form.Control
              type="email"
              name="newEmail"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com"
              disabled={loading}
              isInvalid={newEmail.length > 0 && !emailValid}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">Invalid email.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="checkbox"
              id="recovery-ack"
              label="Identity verified"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={loading}
            />
          </Form.Group>

          {result && (
            <Alert variant={result.variant} className="mt-3">
              {result.text}
            </Alert>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button variant="secondary" onClick={onHide} disabled={loading} className="mr-2">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {loading ? 'Linking...' : 'Link email'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
}
